import { apiFetch, getCsrfToken, ApiError } from './client'

export type MediaSource = 'ManualUpload' | 'VideoAi'

export type PublishSurface =
  | 'YouTubeVideo'
  | 'YouTubeShorts'
  | 'InstagramReels'
  | 'FacebookReels'
  | 'TikTok'

export type MediaIssueSeverity = 'Blocking' | 'Warning'

export interface MediaIssue {
  severity: MediaIssueSeverity
  message: string
}

export interface SurfaceCompatibility {
  surface: PublishSurface
  canPublish: boolean
  issues: MediaIssue[]
}

export interface MediaAsset {
  id: string
  contentType: string
  sizeBytes: number
  width: number | null
  height: number | null
  durationSeconds: number | null
  source: MediaSource
  createdAt: string
  /** Passar filen alla fyra ytor? En rendering, fyra publiceringar. */
  isUniversalMaster: boolean
  surfaces: SurfaceCompatibility[]
}

/** Reglerna bor i PlatformMediaSpecs på servern. Här bara namnen. */
export const SURFACE_LABELS: Record<PublishSurface, string> = {
  YouTubeVideo: 'YouTube',
  YouTubeShorts: 'YouTube Shorts',
  InstagramReels: 'Instagram Reels',
  FacebookReels: 'Facebook Reels',
  TikTok: 'TikTok',
}

/** Speglar MediaEndpoints.AllowedExtensions. Grovfilter — ffprobe är den bindande kontrollen. */
export const ALLOWED_EXTENSIONS = ['mp4', 'mov', 'm4v', 'webm', 'jpg', 'jpeg', 'png'] as const

/** Speglar Media__MaxUploadBytes. Nginx avvisar större redan innan Kestrel ser dem. */
export const MAX_UPLOAD_BYTES = 512 * 1024 * 1024

/**
 * Det avslutande snedstrecket är INTE kosmetik.
 *
 * Endpointen ligger på `/api/media/` (MapGroup("/api/media") + MapPost("/")).
 * Utan snedstrecket svarar nginx 301 — och en 301 på en POST skriver webbläsaren
 * om till GET och släpper kroppen. Uppladdningen blev tyst en tom GET som
 * returnerade medialistan, och `resolve(body as MediaAsset)` la en array i state.
 *
 * Dessutom matchade nginx-blocket `location /api/media/` aldrig `/api/media`, så
 * uppladdningen ramlade ned i `location /` och fick 32 MB-taket i stället för
 * 512 MB. Ett tecken, två fel. Upptäckt i produktion 2026-07-10.
 */
const MEDIA_ROOT = '/api/media/'

export const mediaApi = {
  list: () => apiFetch<MediaAsset[]>(MEDIA_ROOT),
  remove: (id: string) => apiFetch<void>(`${MEDIA_ROOT}${id}`, { method: 'DELETE' }),
  upload: uploadWithProgress,
}

/**
 * Uppladdning kan inte gå genom apiFetch: den hårdkodar application/json och
 * JSON.stringify:ar kroppen. Och fetch() saknar helt uppladdningsprogress —
 * för en halv gigabyte video är en snurra utan besked inte acceptabelt.
 *
 * XMLHttpRequest ger `upload.onprogress` och går att avbryta.
 */
function uploadWithProgress(
  file: File,
  onProgress: (percent: number) => void,
  signal?: AbortSignal,
): Promise<MediaAsset> {
  return getCsrfToken().then(
    (token) =>
      new Promise<MediaAsset>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('POST', MEDIA_ROOT) // snedstrecket är bärande — se MEDIA_ROOT
        xhr.setRequestHeader('X-CSRF-TOKEN', token)
        // Content-Type sätts medvetet INTE. Webbläsaren måste själv lägga på
        // multipart/form-data med rätt boundary; sätter vi headern går den förlorad.

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
        })

        xhr.addEventListener('load', () => {
          let body: unknown = null
          try { body = JSON.parse(xhr.responseText) } catch { /* tomt eller icke-JSON */ }
          if (xhr.status >= 200 && xhr.status < 300) resolve(body as MediaAsset)
          else reject(new ApiError(xhr.status, body))
        })

        xhr.addEventListener('error', () =>
          reject(new Error('Nätverksfel under uppladdningen.')))
        xhr.addEventListener('abort', () =>
          reject(new DOMException('Uppladdningen avbröts.', 'AbortError')))

        signal?.addEventListener('abort', () => xhr.abort(), { once: true })

        const form = new FormData()
        form.append('file', file) // fältnamnet måste matcha [FromForm] IFormFile file
        xhr.send(form)
      }),
  )
}

// ---- presentation ----

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const mb = bytes / (1024 * 1024)
  if (mb < 1) return `${Math.round(bytes / 1024)} kB`
  if (mb < 1024) return `${mb.toFixed(1)} MB`
  return `${(mb / 1024).toFixed(2)} GB`
}

export function formatDuration(seconds: number | null): string | null {
  if (seconds === null) return null
  const total = Math.round(seconds)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/** Reducerat bildförhållande, t.ex. 1080×1920 → "9:16". Fakta, inte bedömning. */
export function aspectRatio(width: number | null, height: number | null): string | null {
  if (!width || !height) return null
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b))
  const d = gcd(width, height)
  return `${width / d}:${height / d}`
}

export function isVideo(contentType: string): boolean {
  return contentType.startsWith('video/')
}
